import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { ConfirmDialog } from "primereact/confirmdialog";
import { useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "context/LanguageContext";
import { useNotification } from "context/NotificationContext";
import {
  renderYesNoField,
  renderRangeField,
  renderSelectField,
  renderInputTextField,
  convertYesNoFieldsToString,
  validateFields,
  handleConfirmSave,
  handleNavigateBack,
  isFieldDifferentFromValues
} from "components/formUtils";

const CirculacaoExtracorporeaForm = () => {
  const { GetTranslation } = useLanguage();
  const notify = useNotification();
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    tipo_canulacao: "Selecione",
    metodo_canulacao_periferica: "",
    assistencia_sem_parada_cardiaca: "",
    canulacao_arterial: "",
    canula_arterial_utilizada: "",
    tamanho_canula_arterial: "",
    canulacao_venosa: "",
    canula_venosa_utilizada: "",
    tamanho_canula_venosa: "",
    numero_estagios_canula_venosa: "",
    lacamento_cavas: "",
    tipo_clampeamento: "Selecione",
    perfusao_cerebral_seletiva: "",
    tempo_cec_min: "",
    tempo_anoxia_clampeamento_min: "",
    tempo_perfusao_cerebral_min: "",
    tempo_total_cirurgia_min: "",
    cardioplegia_utilizada: "",
    necessidade_repetir_cardioplegia: "",
    tipo_infusao_cardioplegia: "",
    temperatura_min_cec: "",
    fluxo_alvo_cec: "",
    necessidade_assistencia_saida_cec: "",
    assistencia_utilizada_saida_cec: "",
    uso_mini_cec: "",
    canula_venosa_superior_utilizada: "",          // Cânula venosa superior utilizada (Marca/Especificação)
    tamanho_canula_venosa_superior: "",            // Tamanho da cânula venosa superior (Fr)
    canula_venosa_inferior_utilizada: "",          // Cânula venosa inferior utilizada (Marca/Especificação)
    tamanho_canula_venosa_inferior: "",            // Tamanho da cânula venosa inferior (Fr)
    tempo_perfusao_cerebral_seletiva: "",          // Tempo de perfusão cerebral seletiva (min)
  });

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (id) {
      fetch(`${process.env.REACT_APP_API_URL}/circulacao_extracoporea/${id}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            const yesNoFields = [
              "assistencia_sem_parada_cardiaca",
              "perfusao_cerebral_seletiva",
              "necessidade_repetir_cardioplegia",
              "necessidade_assistencia_saida_cec",
              "uso_mini_cec",
              "lacamento_cavas",
            ];
            const convertedData = convertYesNoFieldsToString(data.data, yesNoFields);
            setFormData(convertedData);
          } else {
            notify({
              type: "error",
              message: data.error || "Erro ao buscar os dados",
            });
          }
        })
        .catch(() => {
          notify({
            type: "error",
            message: "Erro ao buscar os dados",
          });
        });
    }
  }, [id, token, notify]);

  const requiredFields = [
    "tipo_canulacao",
    "tipo_clampeamento",
    "tempo_total_cirurgia_min",
  ];

  const apiUrl = id
    ? `${process.env.REACT_APP_API_URL}/circulacao_extracoporea/update/${id}`
    : `${process.env.REACT_APP_API_URL}/circulacao_extracoporea/add`;

  const handleSubmit = () => {
    const method = id ? "PUT" : "POST";
    fetch(apiUrl, {
      method: method,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(formData),
    })
      .then((response) => {
        if (response.ok) {
          notify({
            type: "success",
            message: id ? "Circulação Extracorpórea atualizada com sucesso!" : "Circulação Extracorpórea adicionada com sucesso!",
          });
        } else {
          response.json().then((data) => {
            notify({
              type: "error",
              message: `Erro: ${data.error}`,
            });
          });
        }
      })
      .catch(() => {
        notify({
          type: "error",
          message: "Erro ao enviar os dados",
        });
      });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const confirmSave = handleConfirmSave(formData, requiredFields, validateFields, GetTranslation, notify, handleSubmit);
  const handleBack = handleNavigateBack(navigate, id);

  return (
    <div className="card p-3">
      <ConfirmDialog />
      <h4>{id ? GetTranslation("Edit_CirculacaoExtracorporea") : GetTranslation("Add_CirculacaoExtracorporea")}</h4>
      <form className="row g-3">
        {/* Tipo de Canulação */}
        {renderSelectField(
          formData,
          handleChange,
          "tipo_canulacao",
          "Tipo de canulação",
          ["Sem canulação", "Periférica", "Central", "Mista"],
          GetTranslation,
          false,
        )}

        {/* Tipo de Clampeamento */}
        {renderSelectField(
          formData,
          handleChange,
          "tipo_clampeamento",
          "Tipo de clampeamento",
          ["Sem clampeamento", "Convencional", "Endoclamp", "Chitwood"],
          GetTranslation,
          false,
        )}

        {/* Tempo Total da Cirurgia */}
        {renderRangeField(
          formData,
          setFormData,
          "tempo_total_cirurgia_min",
          "Tempo total da cirurgia",
          0,
          2000,
          0,
          GetTranslation,
          "(min, 'de pele a pele')"
        )}

        {/* Método de Canulação Periférica */}
        {(formData.tipo_canulacao === "Periférica" || formData.tipo_canulacao === "Mista") &&
          renderSelectField(
            formData,
            handleChange,
            "metodo_canulacao_periferica",
            "Método de canulação periférica",
            ["Punção", "Dissecção", "Punção por visão direta"],
            GetTranslation
          )}

        {/* Assistência sem Parada Cardíaca */}
        {isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) && 
          renderYesNoField(
            formData,
            handleChange,
            "assistencia_sem_parada_cardiaca",
            "Assistência sem parada cardíaca",
            GetTranslation,
            "(beating heart surgery)"
          )}

        {/* Canulação Arterial */}
        {isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderSelectField(
            formData,
            handleChange,
            "canulacao_arterial",
            "Canulação arterial",
            ["Aorta ascendente", "Arco aórtico", "Tronco braquiocefálico", "Axilar", "Subclávia", "Carotídea", "Femoral", "Ilíaca externa"],
            GetTranslation
          )}

        {/* Cânula Arterial Utilizada */}
        {isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderInputTextField(formData, handleChange, "canula_arterial_utilizada", "Cânula arterial utilizada", GetTranslation, "(Marca/especificação)")}

        {/* Tamanho da Cânula Arterial (Fr) */}
        {isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderRangeField(formData, setFormData, "tamanho_canula_arterial", "Tamanho da cânula arterial", 0, 50, 1, GetTranslation, "(Fr)")}

        {/* Canulação Venosa */}
        {isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderSelectField(
            formData,
            handleChange,
            "canulacao_venosa",
            "Canulação venosa",
            ["Atrial única", "Bicaval", "Femoral única", "Jugular única", "Jugular + femoral", "Cava superior + femoral", "Bicaval + cava esquerda", "Outra"],
            GetTranslation
          )}

        {/* Perfusão Cerebral Seletiva */}
        {isFieldDifferentFromValues(formData, "tipo_clampeamento", ["Sem clampeamento"]) &&
          renderYesNoField(
            formData,
            handleChange,
            "perfusao_cerebral_seletiva",
            "Perfusão cerebral seletiva",
            GetTranslation,
          )}

        {/* Tempo de CEC */}
        {isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderRangeField(formData, setFormData, "tempo_cec_min", "Tempo de CEC", 0, 2000, 0, GetTranslation, "(min)")}

        {/* Tempo de Anóxia/Clampeamento */}
        {isFieldDifferentFromValues(formData, "tipo_clampeamento", ["Sem clampeamento"]) &&
          renderRangeField(formData, setFormData, "tempo_anoxia_clampeamento_min", "Tempo de anóxia/clampeamento", 0, 1000, 0, GetTranslation, "(min)")}

        {/* Cardioplegia Utilizada */}
        {isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderSelectField(
            formData,
            handleChange,
            "cardioplegia_utilizada",
            "Cardioplegia utilizada",
            ["Cirurgia em assistência", "Del Nido", "Custodiol", "Outra"],
            GetTranslation
          )}

        {/* Necessidade de Repetir Cardioplegia */}
        {isFieldDifferentFromValues(formData, "cardioplegia_utilizada", ["Cirurgia em assistência"]) && isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderYesNoField(formData, handleChange, "necessidade_repetir_cardioplegia", "Necessidade de repetir cardioplegia", GetTranslation)}

        {/* Tipo de Infusão da Cardioplegia */}
        {isFieldDifferentFromValues(formData, "cardioplegia_utilizada", ["Cirurgia em assistência"]) &&
          renderSelectField(
            formData,
            handleChange,
            "tipo_infusao_cardioplegia",
            "Tipo de infusão da cardioplegia",
            ["Anterógrada", "Retrógrada", "Combinada"],
            GetTranslation
          )}

        {/* Temperatura Mínima em CEC */}
        {isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderRangeField(formData, setFormData, "temperatura_min_cec", "Temperatura mínima em CEC (ºC)", 10, 40, 0, GetTranslation)}

        {/* Fluxo Alvo da CEC */}
        {isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderRangeField(formData, setFormData, "fluxo_alvo_cec", "Fluxo alvo da CEC (ml/min)", 0, 10000, 0, GetTranslation)}

        {/* Necessidade de Assistência para Saída de CEC */}
        {isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderYesNoField(formData, handleChange, "necessidade_assistencia_saida_cec", "Necessidade de assistência para saída de CEC", GetTranslation)}

        {/* Uso de MiniCEC */}
        {isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderYesNoField(formData, handleChange, "uso_mini_cec", "Uso de miniCEC", GetTranslation)}

        {/* Cânula Venosa Única Utilizada */}
        {(formData.canulacao_venosa === "Atrial única" || formData.canulacao_venosa === "Femoral única" || formData.canulacao_venosa === "Jugular única") && isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderInputTextField(formData, handleChange, "canula_venosa_utilizada", "Cânula venosa única utilizada", GetTranslation, "(Marca/Especificação)")}

        {/* Tamanho da Cânula Venosa Única */}
        {(formData.canulacao_venosa === "Atrial única" || formData.canulacao_venosa === "Femoral única" || formData.canulacao_venosa === "Jugular única") && isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderRangeField(formData, setFormData, "tamanho_canula_venosa", "Tamanho da cânula venosa única", 0, 50, 1, GetTranslation, "(Fr)")}

        {/* Número de Estágios da Cânula Venosa Única */}
        {(formData.canulacao_venosa === "Atrial única" || formData.canulacao_venosa === "Femoral única" || formData.canulacao_venosa === "Jugular única") && isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderSelectField(
            formData,
            handleChange,
            "numero_estagios_canula_venosa",
            "Número de estágios da cânula única",
            ["Estágio único", "Duplo estágio", "Triplo estágio", "multiperfurada"],
            GetTranslation
          )}

        {/* Cânula Venosa Superior Utilizada */}
        {isFieldDifferentFromValues(formData, "canulacao_venosa", ["Atrial única", "Femoral única", "Jugular única"]) && isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderInputTextField(formData, handleChange, "canula_venosa_superior_utilizada", "Cânula venosa superior utilizada", GetTranslation, "(Marca/Especificação)")}

        {/* Tamanho da Cânula Venosa Superior */}
        {isFieldDifferentFromValues(formData, "canulacao_venosa", ["Atrial única", "Femoral única", "Jugular única"]) && isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderRangeField(formData, setFormData, "tamanho_canula_venosa_superior", "Tamanho da cânula venosa superior", 0, 50, 1, GetTranslation, "(Fr)")}

        {/* Tempo de Perfusão Cerebral Seletiva */}
        {formData.perfusao_cerebral_seletiva === "Sim" && isFieldDifferentFromValues(formData, "tipo_clampeamento", ["Sem clampeamento"]) &&
          renderRangeField(formData, setFormData, "tempo_perfusao_cerebral_min", "Tempo de perfusão cerebral seletiva", 0, 1000, 0, GetTranslation, "(min)")}

        {/* Assistência Utilizada na Saída de CEC */}
        {formData.necessidade_assistencia_saida_cec === "Sim" && isFieldDifferentFromValues(formData, "tipo_canulacao", ["Sem canulação"]) &&
          renderSelectField(
            formData,
            handleChange,
            "assistencia_utilizada_saida_cec",
            "Assistência utilizada na saída de CEC",
            ["Balão intra-aórtico", "Centrimag", "ECMO VA", "ECMO VV", "Berlin Heart", "HeartMate"],
            GetTranslation
          )}

        <div className="col-12 d-flex justify-content-between">
          <Button label={GetTranslation("Voltar")} className="btn btn-secondary" onClick={handleBack} />
          <Button label={id ? GetTranslation("Update") : GetTranslation("Create")} className="btn btn-primary" type="submit" onClick={confirmSave} />
        </div>
      </form>
    </div>
  );
};

export default CirculacaoExtracorporeaForm;
